import { useEffect, useMemo, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronDown, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel, Empty } from "@/components/PageHeader";
import { ProductTable, tone } from "@/components/flyers/ProductTable";
import { FlyerAiComment } from "@/components/flyers/FlyerAiComment";
import { useAuth } from "@/context/AuthContext";
import { METRICS, StoreTable, classify } from "@/components/flyers/StoreTable";
import { api, eur, errMsg, fdate } from "@/lib/api";
import { downloadCsv, nfmt, pctFmt } from "@/lib/csv";

export const StorePicker = ({ options, value, onChange }) => {
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState(value);
  const list = options.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover onOpenChange={(o) => o && setDraft(value)}>
      <PopoverTrigger asChild><Button variant="outline" className="h-9 w-full justify-between font-normal" data-testid="flyer-stores-picker">{value.length ? `${value.length} sedi selezionate` : "Tutte le sedi"}<ChevronDown className="h-4 w-4 opacity-50" /></Button></PopoverTrigger>
      <PopoverContent className="w-80 bg-white p-2" align="start">
        <Input placeholder="Cerca sede" className="mb-2 h-8" value={q} onChange={(e) => setQ(e.target.value)} data-testid="flyer-stores-search" />
        <div className="max-h-64 space-y-1 overflow-auto">{list.map((o) => (
          <label key={o.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-slate-50">
            <Checkbox checked={draft.includes(o.id)} data-testid={`flyer-store-opt-${o.id}`} onCheckedChange={(v) => setDraft(v ? [...draft, o.id] : draft.filter((x) => x !== o.id))} /><span className="truncate">{o.name}</span></label>))}</div>
        <div className="mt-2 flex justify-between border-t border-slate-100 pt-2"><Button size="sm" variant="ghost" onClick={() => setDraft([])}>Nessuna</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="flyer-stores-apply" onClick={() => onChange(draft)}>Applica</Button></div>
      </PopoverContent>
    </Popover>
  );
};

const Kpi = ({ id, label, value, delta, cmp, inv }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`flyer-kpi-${id}`}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1.5 font-heading text-xl font-semibold text-slate-900" data-testid={`flyer-kpi-${id}-value`}>{value}</div>
    <div className="mt-0.5 text-[11px]"><span className={tone(delta, inv)} data-testid={`flyer-kpi-${id}-delta`}>{pctFmt(delta)}</span><span className="text-slate-400"> · prima {cmp}</span></div>
  </div>
);

const Kpis = ({ d, groups, groupsCmp }) => {
  const k = d.kpi, c = d.kpi_cmp, x = d.kpi_delta;
  const below = groups.below.length, belowC = groupsCmp.below.length;
  const items = [["revenue", "Incasso promo", eur(k.revenue), eur(c.revenue)], ["pieces", "Pezzi venduti", nfmt(k.pieces, 0), nfmt(c.pieces, 0)],
    ["receipts", "Scontrini con promo", nfmt(k.receipts, 0), nfmt(c.receipts, 0)], ["chain_share", "% scontrini catena", `${nfmt(k.chain_share, 2)}%`, `${nfmt(c.chain_share, 2)}%`],
    ["cards", "Carte fedeltà", nfmt(k.cards, 0), nfmt(c.cards, 0)], ["avg_price", "Prezzo medio effettivo", eur(k.avg_price), eur(c.avg_price)]];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
      {items.map(([id, l, v, cv]) => <Kpi key={id} id={id} label={l} value={v} cmp={cv} delta={x[id]} />)}
      <Kpi id="below" label="Sedi sotto media" value={below} cmp={belowC} delta={belowC ? Math.round(((below - belowC) / belowC) * 1000) / 10 : null} inv />
    </div>
  );
};

const Daily = ({ d, name }) => {
  const data = d.days.map((x) => ({ ...x, label: fdate(x.day).slice(0, 6) }));
  const csv = () => downloadCsv(`andamento-${name}`, ["Giorno", "Incasso", "Pezzi", "Scontrini", "Giorno confronto", "Incasso confronto", "Pezzi confronto", "Scontrini confronto"],
    d.days.map((x) => [x.day, x.revenue, x.pieces, x.receipts, x.cmp_day, x.cmp_revenue, x.cmp_pieces, x.cmp_receipts]));
  return (
    <Panel testId="flyer-daily-chart" title="Andamento giornaliero" action={<Button size="sm" variant="ghost" data-testid="flyer-daily-csv" onClick={csv}><Download className="mr-1 h-4 w-4" />CSV</Button>}>
      <div className="h-72 p-4"><ResponsiveContainer><ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => nfmt(v, 0)} />
        <Tooltip formatter={(v, n) => [n.includes("Incasso") ? eur(v) : nfmt(v), n]} labelFormatter={(l, p) => p?.[0] ? `${fdate(p[0].payload.day)} (confronto ${fdate(p[0].payload.cmp_day)})` : l} />
        <Legend wrapperStyle={{ fontSize: 12 }} /><Bar dataKey="revenue" name="Incasso promo" fill="#2563eb" radius={[3, 3, 0, 0]} />
        <Line dataKey="cmp_revenue" name="Incasso periodo di confronto" stroke="#f59e0b" strokeWidth={2} dot={false} /></ComposedChart></ResponsiveContainer></div>
    </Panel>
  );
};

export const FlyerAnalysis = ({ flyer }) => {
  const { user } = useAuth();
  const [compare, setCompare] = useState("prev");
  const [stores, setStores] = useState([]);
  const [eans, setEans] = useState("");
  const [metric, setMetric] = useState("pct_receipts");
  const [thr, setThr] = useState(20);
  const [opts, setOpts] = useState([]);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let live = true;
    setLoading(true); setErr("");
    api.get(`/flyers/${flyer.id}/analysis`, { params: { compare, stores: stores.join(","), eans } })
      .then((r) => { if (!live) return; setD(r.data); if (!stores.length) setOpts(r.data.stores.filter((s) => s.id).map((s) => ({ id: String(s.id), name: s.name })).sort((a, b) => a.name.localeCompare(b.name))); })
      .catch((e) => live && setErr(errMsg(e))).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [flyer.id, flyer.items, compare, stores, eans]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => d && classify(d.stores, d.chain, metric, thr), [d, metric, thr]);
  const groupsCmp = useMemo(() => d && classify(d.stores_cmp, d.chain_cmp, metric, thr), [d, metric, thr]);
  const cmpLabel = compare === "yoy" ? "anno prima" : "periodo prec.";
  return (
    <div className="space-y-6">
      <Panel testId="flyer-controls">
        <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-4">
          <label className="text-xs text-slate-500">Confronta con
            <Select value={compare} onValueChange={setCompare}><SelectTrigger className="mt-1" data-testid="flyer-compare-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="prev">Periodo precedente di pari durata</SelectItem><SelectItem value="yoy">Stesso periodo anno prima</SelectItem></SelectContent></Select></label>
          <label className="text-xs text-slate-500">Sedi<div className="mt-1"><StorePicker options={opts} value={stores} onChange={setStores} /></div></label>
          <label className="text-xs text-slate-500">Misura sedi vs media
            <Select value={metric} onValueChange={setMetric}><SelectTrigger className="mt-1" data-testid="flyer-metric-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(METRICS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></label>
          <label className="text-xs text-slate-500">Sotto media se inferiore di oltre (%)
            <Input type="number" min={0} max={100} className="mt-1" data-testid="flyer-threshold-input" value={thr} onChange={(e) => setThr(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} /></label>
        </div>
        {d && <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500" data-testid="flyer-periods">Periodo {fdate(d.period.start)} – {fdate(d.period.end)} · confronto {fdate(d.compare.start)} – {fdate(d.compare.end)} · {d.matched} prodotti analizzati{eans ? " (filtrati)" : ""}{loading && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin" />}</p>}
      </Panel>
      {err && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="flyer-analysis-error">{err}</div>}
      {!d && loading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>}
      {d && !d.matched && <Panel><Empty text="Nessun prodotto abbinato: abbina i prodotti nella scheda 'Prodotti e abbinamenti'" /></Panel>}
      {d && d.matched > 0 && <>
        <Kpis d={d} groups={groups} groupsCmp={groupsCmp} />
        <Daily d={d} name={flyer.name} />
        <FlyerAiComment flyer={flyer} canWrite={user?.permissions?.flyers === "write"} params={{ compare, stores: stores.join(","), metric, threshold: thr }} />
        <ProductTable key={d.products.map((p) => p.row + String(p.eans)).join()} rows={d.products} cmpLabel={cmpLabel} name={flyer.name} onApply={setEans} filtered={!!eans} />
        <StoreTable groups={groups} metric={metric} name={flyer.name} />
      </>}
    </div>
  );
};
